import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faCheck, faBoxArchive, faUndo, faTrash, faCalendarDay, faUserCircle, faPen, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';
import { formatDate } from '../../../../utils/dateUtils';
import { Badge, Button, Dropdown, IconButton } from '../../../ui';
import { TASK_STATUSES, taskStatusMeta, type ClientTaskRow } from './taskMeta';

interface ArchivedTaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: ClientTaskRow | null;
  onTaskUpdated: () => void;
  onDelete: (taskId: string) => void;
}

export default function ArchivedTaskDetailModal({
  isOpen,
  onClose,
  task,
  onTaskUpdated,
  onDelete,
}: ArchivedTaskDetailModalProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    status: '',
    due_date: '',
  });

  // Initialize form when task changes
  useEffect(() => {
    if (task) {
      setEditForm({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'Scheduled',
        due_date: task.due_date ? formatDate(task.due_date, 'yyyy-MM-dd') : '',
      });
    }
    setError(null);
    setSuccess(null);
    setIsEditMode(false);
  }, [task]);

  const handleSaveChanges = async () => {
    if (!task) return;

    setIsSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('client_tasks')
        .update({
          title: editForm.title,
          description: editForm.description || null,
          status: editForm.status,
          due_date: editForm.due_date || null,
        })
        .eq('id', task.id);

      if (updateError) throw updateError;

      setSuccess('Task updated successfully!');
      setIsEditMode(false);
      onTaskUpdated();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating archived task:', err);
      setError('Failed to update task. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnarchive = async () => {
    if (!task) return;

    if (!confirm('Are you sure you want to unarchive this task? It will be moved back to the active tasks list.')) {
      return;
    }

    setIsUnarchiving(true);
    setError(null);

    try {
      const { error: unarchiveError } = await supabase
        .from('client_tasks')
        .update({
          archived: false,
          archived_at: null,
        })
        .eq('id', task.id);

      if (unarchiveError) throw unarchiveError;

      setSuccess('Task unarchived successfully!');
      onTaskUpdated();
      onClose();
    } catch (err) {
      console.error('Error unarchiving task:', err);
      setError('Failed to unarchive task. Please try again.');
    } finally {
      setIsUnarchiving(false);
    }
  };

  const handleDelete = () => {
    if (!task) return;
    onDelete(task.id);
  };

  const handleCancel = () => {
    if (task) {
      setEditForm({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'Scheduled',
        due_date: task.due_date ? formatDate(task.due_date, 'yyyy-MM-dd') : '',
      });
    }
    setIsEditMode(false);
    setError(null);
  };

  if (!task) return null;

  const meta = taskStatusMeta(task.status);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
        <Dialog.Panel
          className="mx-auto max-w-2xl w-full max-h-[90dvh] overflow-y-auto"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--space-6)',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
                <FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: '10px', marginRight: '7px' }} />
                Archived Task
              </div>
              {isEditMode ? (
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="ck-title-input"
                  aria-label="Task title"
                  placeholder="Task title..."
                />
              ) : (
                <Dialog.Title
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                    letterSpacing: 'var(--tracking-tight)',
                    color: 'var(--text-body)',
                  }}
                >
                  {task.title}
                </Dialog.Title>
              )}
            </div>

            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              {!isEditMode && (
                <IconButton icon="pencil" label="Edit task" size="sm" onClick={() => setIsEditMode(true)} />
              )}
              <IconButton icon="x" label="Close" size="sm" onClick={onClose} />
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div
              style={{
                marginBottom: '16px',
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
              <button
                onClick={() => setError(null)}
                className="hover-underline"
                style={{
                  marginLeft: '8px',
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  fontSize: 'var(--text-xs)',
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {success && (
            <div
              style={{
                marginBottom: '16px',
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-up-soft)',
                border: '1px solid rgba(86, 162, 104, 0.25)',
                color: 'var(--status-up)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {success}
            </div>
          )}

          {/* Task Badges */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '22px' }}>
            {isEditMode ? (
              <span style={{ width: '160px' }}>
                <Dropdown
                  size="sm"
                  options={TASK_STATUSES.map((s) => ({ value: s, label: s }))}
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  placeholder="Status"
                />
              </span>
            ) : (
              <Badge tone={meta.tone} dot>{task.status}</Badge>
            )}

            {isEditMode ? (
              <span style={{ width: '160px' }}>
                <input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  className="ck-date-input"
                  style={{ height: 'var(--control-sm)' }}
                />
              </span>
            ) : task.due_date ? (
              <Badge tone="neutral">
                <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '9px', marginRight: '5px' }} />
                Due {formatDate(task.due_date)}
              </Badge>
            ) : null}

            {task.assigned_to_name && (
              <Badge tone="neutral">
                <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '9px', marginRight: '5px' }} />
                {task.assigned_to_name}
              </Badge>
            )}

            {task.website_title && (
              <Link to={`/private-network/${task.website_id}`} onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                <Badge tone="accent">
                  {task.website_title}
                  <ExternalLink size={10} style={{ marginLeft: '4px' }} />
                </Badge>
              </Link>
            )}

            {task.archived_at ? (
              <Badge tone="warn">Archived {formatDate(task.archived_at)}</Badge>
            ) : null}
          </div>

          {/* Task Description */}
          <div style={{ marginBottom: '22px' }}>
            <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Description</div>
            {isEditMode ? (
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={4}
                placeholder="Task description..."
                className="ck-textarea"
              />
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-sm)',
                  color: task.description ? 'var(--text-muted)' : 'var(--text-faint)',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.55,
                }}
              >
                {task.description || 'No description provided'}
              </p>
            )}
          </div>

          {/* Task Metadata */}
          <div
            className="t-num"
            style={{
              marginBottom: '22px',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
            }}
          >
            <span>Created: {formatDate(task.created_at)}</span>
            <span>Last Updated: {formatDate(task.updated_at)}</span>
            {task.archived_at && <span>Archived: {formatDate(task.archived_at)}</span>}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button size="sm" variant="secondary" onClick={handleUnarchive} disabled={isUnarchiving}>
                <FontAwesomeIcon
                  icon={isUnarchiving ? faSpinner : faUndo}
                  className={isUnarchiving ? 'animate-spin' : undefined}
                  style={{ fontSize: '12px', marginRight: '7px' }}
                />
                Unarchive
              </Button>
              <Button size="sm" variant="danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px', marginRight: '7px' }} />
                Delete
              </Button>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {isEditMode ? (
                <>
                  <Button size="sm" variant="ghost" onClick={handleCancel} disabled={isSaving}>
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '12px', marginRight: '7px' }} />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveChanges} disabled={isSaving || !editForm.title.trim()}>
                    <FontAwesomeIcon
                      icon={isSaving ? faSpinner : faCheck}
                      className={isSaving ? 'animate-spin' : undefined}
                      style={{ fontSize: '12px', marginRight: '7px' }}
                    />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setIsEditMode(true)}>
                  <FontAwesomeIcon icon={faPen} style={{ fontSize: '12px', marginRight: '7px' }} />
                  Edit Task
                </Button>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
